import { useEffect, useState } from "react";
import { api, fmtDate, openBlob } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Eye, Check, X } from "lucide-react";
import { toast } from "sonner";

const fieldCls = "bg-black/30 border-white/10 text-white";
const err = (e) => toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Check the entered values and try again.");

function PortalTab({ b, onChange }) {
  const [email, setEmail] = useState(b.email || "");
  const [pw, setPw] = useState("");
  const acc = b.portal_account;
  const create = () =>
    api.post(`/borrowers/${b.id}/portal-account`, { email, password: pw })
      .then(() => { toast.success("Portal login created"); onChange(); }).catch(err);
  const setStatus = (status) =>
    api.patch(`/portal-accounts/${acc.user_id}`, { status })
      .then(() => { toast.success(`Account ${status}`); onChange(); }).catch(err);
  if (acc)
    return (
      <div className="space-y-3 text-sm" data-testid="portal-account-info">
        <div className="text-slate-300">Login: <b className="text-white">{acc.email}</b></div>
        <div className="text-slate-300">Status: <b className="text-emerald-300" data-testid="portal-account-status">{acc.status}</b></div>
        <div className="flex gap-2">
          {acc.status !== "active" && <Button size="sm" onClick={() => setStatus("active")} data-testid="portal-approve-button" className="bg-emerald-600 hover:bg-emerald-500">Approve / Activate</Button>}
          {acc.status !== "disabled" && <Button size="sm" variant="secondary" onClick={() => setStatus("disabled")} data-testid="portal-disable-button">Disable</Button>}
        </div>
      </div>
    );
  return (
    <div className="space-y-3">
      <div><Label className="text-xs text-slate-400">Login email</Label>
        <Input value={email} onChange={(e) => setEmail(e.target.value)} data-testid="portal-create-email" className={fieldCls} /></div>
      <div><Label className="text-xs text-slate-400">Temporary password (min 8)</Label>
        <Input value={pw} onChange={(e) => setPw(e.target.value)} data-testid="portal-create-password" className={fieldCls} /></div>
      <Button onClick={create} disabled={!email || pw.length < 8} data-testid="portal-create-button" className="bg-emerald-600 hover:bg-emerald-500">Create portal login</Button>
    </div>
  );
}

function ProfileTab({ b, onChange }) {
  const [score, setScore] = useState(String(b.credit_score ?? ""));
  const [credit, setCredit] = useState(String(b.available_credit ?? ""));
  const [verified, setVerified] = useState(!!b.id_verified);
  const [busy, setBusy] = useState(false);
  const valid = (score === "" || (Number.isInteger(+score) && +score >= 0 && +score <= 1000))
    && (credit === "" || (Number.isFinite(+credit) && +credit >= 0));
  const save = async () => {
    if (!valid) return;
    const payload = { id_verified: verified };
    if (score !== String(b.credit_score ?? "")) payload.credit_score = score === "" ? null : +score;
    if (credit !== String(b.available_credit ?? "")) payload.available_credit = credit === "" ? null : +credit;
    setBusy(true);
    try {
      await api.patch(`/borrowers/${b.id}`, payload);
      toast.success("Profile updated"); await onChange();
    } catch (e) { err(e); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div><Label htmlFor="profile-credit-score" className="text-xs text-slate-400">Credit score</Label>
          <Input id="profile-credit-score" type="number" min="0" max="1000" step="1" placeholder="500 (default)" value={score} onChange={(e) => setScore(e.target.value)} data-testid="profile-credit-score" className={fieldCls} /></div>
        <div><Label htmlFor="profile-available-credit" className="text-xs text-slate-400">Available credit (₱)</Label>
          <Input id="profile-available-credit" type="number" min="0" step="0.01" placeholder="500 (default)" value={credit} onChange={(e) => setCredit(e.target.value)} data-testid="profile-available-credit" className={fieldCls} /></div>
      </div>
      <p data-testid="profile-default-hint" className="text-xs text-amber-200/80 leading-relaxed">Blank credit fields use the initial 500 defaults, labeled unverified in the portal. Enter only authorized, recorded values. Verifying an ID does not approve credit.</p>
      {!valid && <p data-testid="profile-credit-error" role="alert" className="text-xs text-rose-300">Use a whole-number score from 0 to 1,000 and a nonnegative credit amount.</p>}
      <label className="flex items-center gap-3 text-sm text-slate-300">
        <Switch checked={verified} onCheckedChange={setVerified} data-testid="profile-id-verified" /> ID verified (shows "Verified Account")
      </label>
      <Button onClick={save} disabled={busy || !valid} data-testid="profile-save-button" className="bg-emerald-600 hover:bg-emerald-500">{busy ? "Saving…" : "Save"}</Button>
    </div>
  );
}

function DocsTab({ b }) {
  const [docs, setDocs] = useState([]);
  const load = () => api.get(`/borrowers/${b.id}/documents`).then((r) => setDocs(r.data));
  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [b.id]);
  const review = (id, s) => api.patch(`/documents/${id}`, null, { params: { review_status: s } }).then(load).catch(err);
  if (!docs.length) return <div className="text-sm text-slate-500 py-4">No documents uploaded yet.</div>;
  return (
    <div className="space-y-2 max-h-72 overflow-y-auto">
      {docs.map((d) => (
        <div key={d.id} className="flex items-center justify-between gap-2 p-2 rounded-lg bg-white/5" data-testid={`admin-doc-${d.id}`}>
          <div className="min-w-0 text-sm">
            <div className="text-white truncate">{d.kind.replace(/_/g, " ")} · {d.original_filename}</div>
            <div className="text-xs text-slate-500">{d.review_status} · {fmtDate(d.created_at)}</div>
          </div>
          <div className="flex gap-1 shrink-0">
            <Button size="icon" variant="ghost" onClick={() => openBlob(`/documents/${d.id}/download`)} data-testid={`admin-doc-view-${d.id}`}><Eye className="h-4 w-4" /></Button>
            <Button size="icon" variant="ghost" onClick={() => review(d.id, "Approved")} data-testid={`admin-doc-approve-${d.id}`} className="text-emerald-400"><Check className="h-4 w-4" /></Button>
            <Button size="icon" variant="ghost" onClick={() => review(d.id, "Rejected")} data-testid={`admin-doc-reject-${d.id}`} className="text-rose-400"><X className="h-4 w-4" /></Button>
          </div>
        </div>
      ))}
    </div>
  );
}

export function BorrowerManageDialog({ borrower, onOpenChange, onChange, initialTab = "portal", onDelete }) {
  return (
    <Dialog open={!!borrower} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#101624] border border-white/10 text-white sm:max-w-lg" data-testid="borrower-manage-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">{borrower?.full_name}</DialogTitle></DialogHeader>
        {borrower && (
          <Tabs key={`${borrower.id}-${initialTab}`} defaultValue={initialTab}>
            <TabsList className="bg-white/5">
              <TabsTrigger value="portal" data-testid="manage-tab-portal">Portal access</TabsTrigger>
              <TabsTrigger value="profile" data-testid="manage-tab-profile">Credit profile</TabsTrigger>
              <TabsTrigger value="docs" data-testid="manage-tab-docs">Documents</TabsTrigger>
            </TabsList>
            <TabsContent value="portal" className="pt-3"><PortalTab b={borrower} onChange={onChange} /></TabsContent>
            <TabsContent value="profile" className="pt-3"><ProfileTab b={borrower} onChange={onChange} /></TabsContent>
            <TabsContent value="docs" className="pt-3"><DocsTab b={borrower} /></TabsContent>
          </Tabs>
        )}
        {borrower && onDelete && <button onClick={() => onDelete(borrower.id)} data-testid={`delete-borrower-${borrower.id}`} className="text-xs text-rose-400 hover:text-rose-300 text-left border-t border-white/10 pt-3 mt-2">Delete member record (only when no loans exist)</button>}
      </DialogContent>
    </Dialog>
  );
}
